import { Control, Point, TMat2D, util } from "fabric";
import { WarpTransformOptions, WarpTransformPoint } from "../../interfaces";

export const TRANSFORM_POINT_INDEX = {
  topLeft: 0,
  topMiddle: 1,
  topRight: 2,
  bottomRight: 3,
  bottomMiddle: 4,
  bottomLeft: 5,
  topLeftHandle: 6,
  topRightHandle: 7,
  bottomLeftHandle: 8,
  bottomRightHandle: 9,
} as const;

export type TransformPointKey = keyof typeof TRANSFORM_POINT_INDEX;

/**
 * Minimal interface that WarpedTextLayer must satisfy to host warp controls.
 * Defined here (not in index.ts) to avoid circular imports.
 */
export interface WarpControlHost {
  readonly width: number;
  readonly height: number;
  oCoords: Record<string, { x: number; y: number }>;
  controls: Record<string, Control>;
  hasBorders: boolean;
  warpPointEditMode: boolean;
  canvas?: { requestRenderAll(): void } | null;
  calcTransformMatrix(): TMat2D;
  getViewportTransform(): TMat2D;
  getCanvasRetinaScaling(): number;
  setControlsVisibility(options: Record<string, boolean>): void;
  /** Return current normalized [0,1] transform options (null if not ready). */
  getWarpTransformOptions(): WarpTransformOptions | null;
  /** Persist updated transform options back to the layer and re-render. */
  updateWarpTransformOptions(options: WarpTransformOptions): void;
}

const renderPoint = (ctx: CanvasRenderingContext2D, left: number, top: number): void => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(left, top, 5, 0, Math.PI * 2);
  ctx.fillStyle = "#9762b0"; // primary-500
  ctx.fill();
  ctx.restore();
};

const makePositionHandler = (capturedKey: TransformPointKey) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function positionHandler(_dim: any, _finalMatrix: any, fabricObject: any) {
    const host = fabricObject as WarpControlHost;
    const transformOptions = host.getWarpTransformOptions();
    const pt = transformOptions?.points[TRANSFORM_POINT_INDEX[capturedKey]] ?? { x: 0.5, y: 0.5 };

    // Normalized [0,1] → local group space (center at 0,0)
    const localX = pt.x * host.width - host.width / 2;
    const localY = pt.y * host.height - host.height / 2;

    // Local → canvas → screen
    return new Point(localX, localY)
      .transform(host.calcTransformMatrix())
      .transform(host.getViewportTransform());
  };

const makeActionHandler = (capturedKey: TransformPointKey) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function actionHandler(_eventData: any, transform: any, x: number, y: number) {
    const host = transform.target as WarpControlHost;
    if (!host.canvas) return false;

    const current = host.getWarpTransformOptions();
    if (!current) return false;

    // Canvas coords → local group space
    const localPoint = new Point(x, y).transform(
      util.invertTransform(host.calcTransformMatrix()),
    );

    // Local → normalized [0,1]
    const nx = (localPoint.x + host.width / 2) / host.width;
    const ny = (localPoint.y + host.height / 2) / host.height;

    const points = current.points.map((point: WarpTransformPoint) => ({ ...point }));
    const index = TRANSFORM_POINT_INDEX[capturedKey];
    const prev = points[index];
    const delta = { x: nx - prev.x, y: ny - prev.y };
    points[index] = { x: nx, y: ny };

    if (capturedKey === "topMiddle") {
      points[TRANSFORM_POINT_INDEX.topLeftHandle] = {
        x: points[TRANSFORM_POINT_INDEX.topLeftHandle].x + delta.x,
        y: points[TRANSFORM_POINT_INDEX.topLeftHandle].y + delta.y,
      };
      points[TRANSFORM_POINT_INDEX.topRightHandle] = {
        x: points[TRANSFORM_POINT_INDEX.topRightHandle].x + delta.x,
        y: points[TRANSFORM_POINT_INDEX.topRightHandle].y + delta.y,
      };
    } else if (capturedKey === "bottomMiddle") {
      points[TRANSFORM_POINT_INDEX.bottomLeftHandle] = {
        x: points[TRANSFORM_POINT_INDEX.bottomLeftHandle].x + delta.x,
        y: points[TRANSFORM_POINT_INDEX.bottomLeftHandle].y + delta.y,
      };
      points[TRANSFORM_POINT_INDEX.bottomRightHandle] = {
        x: points[TRANSFORM_POINT_INDEX.bottomRightHandle].x + delta.x,
        y: points[TRANSFORM_POINT_INDEX.bottomRightHandle].y + delta.y,
      };
    }

    host.updateWarpTransformOptions({ ...current, points });
    return true;
  };

function drawWarpRails(host: WarpControlHost, ctx: CanvasRenderingContext2D): void {
  const getPos = (key: TransformPointKey): { x: number; y: number } =>
    host.oCoords[`warp_${key}`] ?? { x: 0, y: 0 };

  const p0 = getPos("topLeft");
  const p1 = getPos("topMiddle");
  const p2 = getPos("topRight");
  const p3 = getPos("bottomRight");
  const p4 = getPos("bottomMiddle");
  const p5 = getPos("bottomLeft");
  const p6 = getPos("topLeftHandle");
  const p7 = getPos("topRightHandle");
  const p8 = getPos("bottomLeftHandle");
  const p9 = getPos("bottomRightHandle");

  ctx.save();
  const s = host.getCanvasRetinaScaling();
  ctx.setTransform(s, 0, 0, s, 0, 0);
  ctx.setLineDash([]);

  ctx.strokeStyle = "#d8b9ea"; // primary-300
  ctx.lineWidth = 1;
  const stemPairs: [{ x: number; y: number }, { x: number; y: number }][] = [
    [p0, p6], [p6, p1], [p1, p7], [p7, p2],
    [p5, p8], [p8, p4], [p4, p9], [p9, p3],
  ];
  for (const [a, b] of stemPairs) {
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }

  ctx.strokeStyle = "#9762b0"; // primary-500
  ctx.lineWidth = 1.5;

  ctx.beginPath();
  ctx.moveTo(p0.x, p0.y);
  ctx.bezierCurveTo(p0.x, p0.y, p6.x, p6.y, p1.x, p1.y);
  ctx.bezierCurveTo(p7.x, p7.y, p2.x, p2.y, p2.x, p2.y);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(p5.x, p5.y);
  ctx.bezierCurveTo(p5.x, p5.y, p8.x, p8.y, p4.x, p4.y);
  ctx.bezierCurveTo(p9.x, p9.y, p3.x, p3.y, p3.x, p3.y);
  ctx.stroke();

  ctx.restore();
}

export function setupWarpPointControls(host: WarpControlHost): void {
  const ANCHOR_KEYS: TransformPointKey[] = [
    "topLeft", "topMiddle", "topRight",
    "bottomRight", "bottomMiddle", "bottomLeft",
  ];
  const HANDLE_KEYS: TransformPointKey[] = [
    "topLeftHandle", "topRightHandle",
    "bottomLeftHandle", "bottomRightHandle",
  ];

  const warpPointControls: Record<string, Control> = {};
  for (const key of [...ANCHOR_KEYS, ...HANDLE_KEYS]) {
    warpPointControls[`warp_${key}`] = new Control({
      actionName: "transformOptions",
      cursorStyle: "crosshair",
      positionHandler: makePositionHandler(key),
      actionHandler: makeActionHandler(key),
      render: renderPoint,
    });
  }

  host.controls = { ...host.controls, ...warpPointControls };
}

export function setWarpPointEditMode(host: WarpControlHost, mode: boolean): void {
  host.warpPointEditMode = mode;

  if (mode) {
    host.hasBorders = false;
    host.setControlsVisibility({
      tl: false, tr: false, bl: false, br: false,
      mt: false, mb: false, ml: false, mr: false, mtr: false,
    });
    for (const key of Object.keys(host.controls)) {
      if (key.startsWith("warp_")) host.controls[key].visible = true;
    }
  } else {
    host.hasBorders = true;
    host.setControlsVisibility({
      tl: true, tr: true, bl: true, br: true,
      mt: false, mb: false, ml: false, mr: false, mtr: true,
    });
    for (const key of Object.keys(host.controls)) {
      if (key.startsWith("warp_")) host.controls[key].visible = false;
    }
  }

  host.canvas?.requestRenderAll();
}

export function drawWarpControls(
  host: WarpControlHost,
  ctx: CanvasRenderingContext2D,
): void {
  if (!host.warpPointEditMode) return;
  drawWarpRails(host, ctx);
}
